import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeftIcon, CheckCircle2Icon, ExternalLinkIcon, Loader2Icon, SparklesIcon } from 'lucide-react';
import { trpc, type RouterOutputs } from '@org/clients/school';
import type { StoryCard, WomanJobStatus, WomanStoryStatus } from '@org/model';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  Input,
  MediaUploader,
  Separator,
  TextArea,
  storyColors,
  useEmojiShortcode,
} from '@org/ui';
import { getMutationErrorMessage } from '../shared/multi-step-utils';
import {
  FieldPair,
  FormField,
  IMAGE_FILE_TYPES,
  MediaField,
  SegmentedToggle,
} from './story-form-fields';
import { StoryCardsEditor } from './story-cards-editor';
import { StoryPreview } from './story-preview';
import {
  buildUpdatePatch,
  clearRequestErrors,
  formFromWoman,
  isFormDirty,
  isHttpUrl,
  isJobRunning,
  peekRequestErrors,
  publishBlockers,
  storyWomanFromForm,
  type WomanFormState,
  type WomanRequestErrors,
  type WomanTextField,
} from './woman-editor-helpers';

const PREVIEW_WIDTH_KEY = 'admin-women-preview-width';
const PREVIEW_DEFAULT_WIDTH = 360;
const PREVIEW_MIN_WIDTH = 300;
const FORM_MIN_WIDTH = 380;

/** Keeps the preview between its minimum and what leaves the form usable. */
function clampPreviewWidth(width: number, container: HTMLElement | null): number {
  const max = container ? Math.max(PREVIEW_MIN_WIDTH, container.clientWidth - FORM_MIN_WIDTH) : 900;
  return Math.round(Math.min(Math.max(width, PREVIEW_MIN_WIDTH), max));
}

function readStoredPreviewWidth(): number {
  try {
    const stored = Number(window.localStorage.getItem(PREVIEW_WIDTH_KEY));
    return stored >= PREVIEW_MIN_WIDTH ? stored : PREVIEW_DEFAULT_WIDTH;
  } catch {
    return PREVIEW_DEFAULT_WIDTH;
  }
}


type WomanDetail = RouterOutputs['women']['getById'];

const POLL_INTERVAL_MS = 3000;

/** Shows the symbol as the student will see it: `:heart:` becomes the emoji. */
function SymbolPreview({ value }: { value: string }) {
  const emoji = useEmojiShortcode(value);
  if (!value.trim()) return null;
  return <span className="text-2xl leading-none">{emoji ?? value}</span>;
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">{children}</CardContent>
    </Card>
  );
}

/** One line telling where an AI job stands, plus its error when it failed. */
function JobStatus({
  status,
  error,
  requestError,
  runningLabel,
  doneLabel,
  failedLabel,
}: {
  status: WomanJobStatus;
  error: string | null | undefined;
  requestError: string | undefined;
  runningLabel: string;
  doneLabel: string;
  failedLabel: string;
}) {
  const { t } = useTranslation('admin');
  return (
    <div className="flex flex-col gap-2" aria-live="polite">
      {isJobRunning(status) ? (
        <p className="flex items-center gap-2 text-sm">
          <Loader2Icon className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
          <span>
            {runningLabel} <span className="text-muted-foreground">{t('women.editor.jobs.takesMinutes')}</span>
          </span>
        </p>
      ) : null}
      {status === 'generated' ? (
        <p className="flex items-center gap-2 text-sm text-green-700">
          <CheckCircle2Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {doneLabel}
        </p>
      ) : null}
      {status === 'failed' ? (
        <Alert variant="destructive">
          <AlertTitle>{failedLabel}</AlertTitle>
          {error ? <AlertDescription>{error}</AlertDescription> : null}
        </Alert>
      ) : null}
      {requestError ? (
        <Alert variant="destructive">
          <AlertTitle>{t('women.editor.jobs.requestFailed')}</AlertTitle>
          <AlertDescription>{requestError}</AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}

export function WomanEditor({ womanId }: { womanId: string }) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const womanQuery = trpc.women.getById.useQuery(
    { womanId },
    {
      // Follow the AI jobs while one is waiting or running.
      refetchInterval: (query) => {
        const data = query.state.data;
        return data && (isJobRunning(data.portraitStatus) || isJobRunning(data.draftStatus))
          ? POLL_INTERVAL_MS
          : false;
      },
    },
  );
  const woman: WomanDetail | undefined = womanQuery.data;

  const [form, setForm] = useState<WomanFormState | null>(null);
  const [saved, setSaved] = useState<WomanFormState | null>(null);
  const [draftWaiting, setDraftWaiting] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [requestErrors, setRequestErrors] = useState<WomanRequestErrors>(() => peekRequestErrors(womanId));
  const [confirmDraft, setConfirmDraft] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  // Latest form for the effect below, which must only react to server changes.
  const latest = useRef<{ form: WomanFormState | null; saved: WomanFormState | null }>({ form, saved });
  latest.current = { form, saved };
  const previousJobs = useRef<{ portrait: WomanJobStatus; draft: WomanJobStatus } | null>(null);

  useEffect(() => {
    if (!woman) return;
    const previous = previousJobs.current;
    previousJobs.current = { portrait: woman.portraitStatus, draft: woman.draftStatus };

    const current = latest.current;
    if (!current.form || !current.saved) {
      const initial = formFromWoman(woman);
      setForm(initial);
      setSaved(initial);
      return;
    }
    if (!previous) return;

    // The AI finished the portrait: show it without touching anything else she typed.
    if (isJobRunning(previous.portrait) && woman.portraitStatus === 'generated') {
      const portraitUrl = woman.portraitUrl ?? '';
      setForm((f) => (f ? { ...f, portraitUrl } : f));
      setSaved((s) => (s ? { ...s, portraitUrl } : s));
    }

    // The AI finished the draft: load it, unless that would throw away unsaved work.
    if (isJobRunning(previous.draft) && woman.draftStatus === 'generated') {
      if (isFormDirty(current.form, current.saved)) {
        setDraftWaiting(true);
      } else {
        const next = formFromWoman(woman);
        setForm(next);
        setSaved(next);
        toast.success(t('women.editor.draft.loaded'));
      }
    }
  }, [woman, t]);

  const updateMutation = trpc.women.update.useMutation();
  const portraitMutation = trpc.women.requestPortrait.useMutation();
  const draftMutation = trpc.women.requestDraft.useMutation();

  const dirty = form && saved ? isFormDirty(form, saved) : false;
  const blockers = useMemo(() => (form ? publishBlockers(form) : []), [form]);
  const storyWoman = useMemo(() => (form ? storyWomanFromForm(form, womanId) : null), [form, womanId]);

  // Width of the preview column on wide screens; the bar between the two
  // columns drags it, and the choice is remembered on this browser.
  const splitRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef(false);
  const [previewWidth, setPreviewWidth] = useState<number>(readStoredPreviewWidth);
  useEffect(() => {
    try {
      window.localStorage.setItem(PREVIEW_WIDTH_KEY, String(previewWidth));
    } catch {
      // storage unavailable: the width just isn't remembered
    }
  }, [previewWidth]);
  if (womanQuery.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">{t('women.editor.loading')}</div>
    );
  }
  if (!woman || !form || !saved || !storyWoman) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-3 text-muted-foreground">
        <span>{womanQuery.isError ? t('women.editor.notFound') : t('women.editor.loading')}</span>
        {womanQuery.isError ? (
          <Button variant="outline" asChild>
            <Link to="/women">{t('women.editor.back')}</Link>
          </Button>
        ) : null}
      </div>
    );
  }

  const setField = (key: WomanTextField | 'taglineEn' | 'taglinePt', value: string) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));
  const setCards = (storyCards: StoryCard[]) => setForm((f) => (f ? { ...f, storyCards } : f));

  const nameMissing = !form.name.trim();
  const publishBlocked = form.storyStatus === 'published' && blockers.length > 0;
  const blockerText = blockers.map((b) => t(`women.editor.publish.blockers.${b}`)).join(' ');

  const textInput = (key: WomanTextField, label: string, hint?: React.ReactNode, extra?: React.ReactNode) => (
    <FormField htmlFor={`woman-${key}`} label={label} hint={hint}>
      <div className="flex items-center gap-2">
        <Input
          id={`woman-${key}`}
          className="min-w-0 flex-1"
          value={form[key]}
          onChange={(event) => setField(key, event.target.value)}
        />
        {extra}
      </div>
    </FormField>
  );

  const handleSave = async () => {
    if (!dirty || nameMissing || publishBlocked) return;
    try {
      const updated = await updateMutation.mutateAsync({ womanId, ...buildUpdatePatch(form, saved) });
      const next = formFromWoman(updated);
      setForm(next);
      setSaved(next);
      setDraftWaiting(false);
      await Promise.all([utils.women.getById.invalidate({ womanId }), utils.women.list.invalidate()]);
      toast.success(t('women.editor.toasts.saved'));
    } catch (error) {
      toast.error(t('women.editor.toasts.saveError', { message: getMutationErrorMessage(error) }));
    }
  };

  const requestPortrait = async () => {
    const referencePhotoUrl = form.referencePhotoUrl.trim();
    if (!isHttpUrl(referencePhotoUrl)) return;
    clearRequestErrors(womanId);
    setRequestErrors((e) => ({ ...e, portrait: undefined }));
    try {
      await portraitMutation.mutateAsync({ womanId, referencePhotoUrl });
      // The server stored the photo with the request.
      setForm((f) => (f ? { ...f, referencePhotoUrl } : f));
      setSaved((s) => (s ? { ...s, referencePhotoUrl } : s));
      await utils.women.getById.invalidate({ womanId });
    } catch (error) {
      setRequestErrors((e) => ({ ...e, portrait: getMutationErrorMessage(error) }));
    }
  };

  const requestDraft = async () => {
    clearRequestErrors(womanId);
    setRequestErrors((e) => ({ ...e, draft: undefined }));
    setDraftWaiting(false);
    try {
      await draftMutation.mutateAsync({ womanId });
      await utils.women.getById.invalidate({ womanId });
    } catch (error) {
      setRequestErrors((e) => ({ ...e, draft: getMutationErrorMessage(error) }));
    }
  };

  const handleDraftClick = () => {
    const replacesWork =
      form.storyCards.length > 0 || (woman.storyCards?.length ?? 0) > 0 || woman.storyStatus === 'published';
    if (replacesWork) setConfirmDraft(true);
    else void requestDraft();
  };

  const loadDraft = () => {
    const next = formFromWoman(woman);
    setForm(next);
    setSaved(next);
    setDraftWaiting(false);
  };

  const portraitRunning = isJobRunning(woman.portraitStatus);
  const draftRunning = isJobRunning(woman.draftStatus);
  const referenceOk = isHttpUrl(form.referencePhotoUrl);
  const sources = woman.sources ?? [];

  const startPreviewResize = (e: React.PointerEvent<HTMLDivElement>) => {
    resizingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const movePreviewResize = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizingRef.current || !splitRef.current) return;
    const right = splitRef.current.getBoundingClientRect().right;
    setPreviewWidth(clampPreviewWidth(right - e.clientX - 10, splitRef.current));
  };
  const endPreviewResize = (e: React.PointerEvent<HTMLDivElement>) => {
    resizingRef.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100svh-9rem)]">
      {/* Header: always visible */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => (dirty ? setConfirmLeave(true) : navigate({ to: '/women' }))}
        >
          <ArrowLeftIcon className="mr-1 h-4 w-4" />
          {t('women.editor.back')}
        </Button>
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold">
          {form.name.trim() || t('women.list.noName')}
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedToggle<WomanStoryStatus>
            ariaLabel={t('women.editor.publish.label')}
            value={form.storyStatus}
            onChange={(storyStatus) => setForm((f) => (f ? { ...f, storyStatus } : f))}
            options={[
              { value: 'draft', label: t('women.list.status.draft') },
              {
                value: 'published',
                label: t('women.list.status.published'),
                disabled: blockers.length > 0 && form.storyStatus !== 'published',
                title: blockers.length > 0 ? blockerText : undefined,
              },
            ]}
          />
          <span
            className={dirty ? 'text-sm font-medium text-amber-700' : 'text-sm text-muted-foreground'}
            aria-live="polite"
          >
            {dirty ? t('women.editor.unsaved') : t('women.editor.allSaved')}
          </span>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!dirty || nameMissing || publishBlocked || updateMutation.isPending}
          >
            {updateMutation.isPending ? t('common.status.saving') : t('women.editor.save')}
          </Button>
        </div>
        <p className="w-full text-xs text-muted-foreground">
          {blockers.length > 0
            ? t('women.editor.publish.blocked', { reasons: blockerText })
            : form.storyStatus === 'published'
              ? t('women.editor.publish.publishedHint')
              : t('women.editor.publish.draftHint')}
        </p>
      </div>

      <div ref={splitRef} className="flex min-h-0 flex-1 flex-col gap-6 lg:flex-row lg:gap-0">
        {/* Left: editing (scrolls on its own on wide screens) */}
        <div className="min-w-0 flex-1 space-y-6 lg:overflow-y-auto lg:pr-2">
          {draftWaiting ? (
            <Alert variant="info">
              <AlertTitle>{t('women.editor.draft.waitingTitle')}</AlertTitle>
              <AlertDescription>
                <p>{t('women.editor.draft.waitingDescription')}</p>
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={loadDraft}>
                  {t('women.editor.draft.loadDiscard')}
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          <Section title={t('women.editor.profile.title')} description={t('women.editor.profile.description')}>
            <FieldPair>
              {textInput('name', t('women.editor.profile.name'), nameMissing ? t('women.editor.profile.nameRequired') : undefined)}
              {textInput('years', t('women.editor.profile.years'), t('women.editor.profile.yearsHint'))}
            </FieldPair>
            <FieldPair>
              {textInput('field', t('women.editor.profile.field'))}
              {textInput('fieldPt', t('women.editor.profile.fieldPt'))}
            </FieldPair>
            <FieldPair>
              {textInput('nationality', t('women.editor.profile.nationality'))}
              {textInput('nationalityPt', t('women.editor.profile.nationalityPt'))}
            </FieldPair>
            <FieldPair>
              {textInput(
                'symbol',
                t('women.editor.profile.symbol'),
                t('women.editor.profile.symbolHint'),
                <SymbolPreview value={form.symbol} />,
              )}
            </FieldPair>
            <FieldPair>
              <FormField htmlFor="woman-quote" label={t('women.editor.profile.quote')}>
                <TextArea
                  id="woman-quote"
                  rows={3}
                  value={form.quote}
                  onChange={(event) => setField('quote', event.target.value)}
                />
              </FormField>
              <FormField htmlFor="woman-quotePt" label={t('women.editor.profile.quotePt')}>
                <TextArea
                  id="woman-quotePt"
                  rows={3}
                  value={form.quotePt}
                  onChange={(event) => setField('quotePt', event.target.value)}
                />
              </FormField>
            </FieldPair>
            {textInput('quoteSource', t('women.editor.profile.quoteSource'), t('women.editor.profile.quoteSourceHint'))}
            <FieldPair>
              {textInput(
                'characteristicEn',
                t('women.editor.profile.characteristicEn'),
                t('women.editor.profile.characteristicEnHint'),
              )}
              {textInput(
                'characteristic',
                t('women.editor.profile.characteristic'),
                t('women.editor.profile.characteristicHint'),
              )}
            </FieldPair>
            <FieldPair>
              <FormField
                htmlFor="woman-taglineEn"
                label={t('women.editor.profile.taglineEn')}
                hint={t('women.editor.profile.taglineHint')}
              >
                <TextArea
                  id="woman-taglineEn"
                  rows={2}
                  value={form.taglineEn}
                  onChange={(event) => setField('taglineEn', event.target.value)}
                />
              </FormField>
              <FormField htmlFor="woman-taglinePt" label={t('women.editor.profile.taglinePt')}>
                <TextArea
                  id="woman-taglinePt"
                  rows={2}
                  value={form.taglinePt}
                  onChange={(event) => setField('taglinePt', event.target.value)}
                />
              </FormField>
            </FieldPair>
          </Section>

          <Section title={t('women.editor.portrait.title')} description={t('women.editor.portrait.description')}>
            <div className="flex flex-col gap-4 sm:flex-row">
              <div
                className="flex h-44 w-44 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border"
                style={{ backgroundColor: storyColors.portraitDisc }}
              >
                {form.portraitUrl.trim() ? (
                  <img
                    src={form.portraitUrl}
                    alt={t('women.editor.portrait.alt', { name: form.name })}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <span className="px-3 text-center text-xs text-muted-foreground">
                    {t('women.editor.portrait.none')}
                  </span>
                )}
              </div>

              <div className="flex min-w-0 flex-1 flex-col gap-3">
                <FormField label={t('women.editor.portrait.reference')} hint={t('women.editor.portrait.referenceHint')}>
                  <MediaField
                    idBase="woman-reference"
                    kind="image"
                    url={form.referencePhotoUrl}
                    onUrlChange={(url) => setField('referencePhotoUrl', url)}
                  />
                </FormField>
                {form.referencePhotoUrl.trim() && !referenceOk ? (
                  <p className="text-xs text-red-600">{t('women.editor.portrait.referenceInvalid')}</p>
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!referenceOk || portraitRunning || portraitMutation.isPending}
                    onClick={requestPortrait}
                  >
                    <SparklesIcon className="mr-1 h-4 w-4" />
                    {form.portraitUrl.trim()
                      ? t('women.editor.portrait.regenerate')
                      : t('women.editor.portrait.generate')}
                  </Button>
                  {portraitRunning ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={!referenceOk || portraitMutation.isPending}
                      onClick={requestPortrait}
                    >
                      {t('women.editor.jobs.retry')}
                    </Button>
                  ) : null}
                </div>
                <JobStatus
                  status={woman.portraitStatus}
                  error={woman.portraitError}
                  requestError={requestErrors.portrait}
                  runningLabel={t('women.editor.portrait.running')}
                  doneLabel={t('women.editor.portrait.done')}
                  failedLabel={t('women.editor.portrait.failed')}
                />
              </div>
            </div>

            <Separator />

            <FormField label={t('women.editor.portrait.uploadOwn')} hint={t('women.editor.portrait.uploadOwnHint')}>
              <MediaUploader
                compact
                acceptedFileTypes={IMAGE_FILE_TYPES}
                onUploadComplete={(url) => setField('portraitUrl', url)}
                onError={(error) => toast.error(t('women.media.uploadError', { message: error.message }))}
              />
            </FormField>
          </Section>

          <Section title={t('women.editor.draft.title')} description={t('women.editor.draft.description')}>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={draftRunning || draftMutation.isPending}
                onClick={handleDraftClick}
              >
                <SparklesIcon className="mr-1 h-4 w-4" />
                {t('women.editor.draft.button')}
              </Button>
              {draftRunning ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={draftMutation.isPending}
                  onClick={() => void requestDraft()}
                >
                  {t('women.editor.jobs.retry')}
                </Button>
              ) : null}
            </div>
            <JobStatus
              status={woman.draftStatus}
              error={woman.draftError}
              requestError={requestErrors.draft}
              runningLabel={t('women.editor.draft.running')}
              doneLabel={t('women.editor.draft.done')}
              failedLabel={t('women.editor.draft.failed')}
            />
            {sources.length > 0 ? (
              <div>
                <h3 className="mb-1 text-sm font-medium">{t('women.editor.draft.sources')}</h3>
                <ul className="space-y-1 text-sm">
                  {sources.map((source, index) => (
                    <li key={`${source.url}-${index}`} className="flex items-start gap-1">
                      <ExternalLinkIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                      {isHttpUrl(source.url) ? (
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="min-w-0 break-words text-blue-700 underline"
                        >
                          {source.title || source.url}
                        </a>
                      ) : (
                        <span className="min-w-0 break-words">{source.title || source.url}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Section>

          <Section title={t('women.cards.title')} description={t('women.cards.description')}>
            <StoryCardsEditor cards={form.storyCards} onChange={setCards} onShowPage={setPreviewIndex} />
          </Section>

          <Section title={t('women.editor.units.title')}>
            {woman.units.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('women.editor.units.none')}</p>
            ) : (
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {woman.units.map((unit) => (
                  <li key={unit.unitId}>
                    <Link
                      to="/units/$unitId"
                      params={{ unitId: unit.unitId }}
                      target="_blank"
                      className="text-blue-700 underline"
                    >
                      {unit.name || t('women.editor.units.unnamed')}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        {/* Drag bar: trades form width for a larger preview (wide screens only) */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('women.preview.resize')}
          title={t('women.preview.resize')}
          tabIndex={0}
          onPointerDown={startPreviewResize}
          onPointerMove={movePreviewResize}
          onPointerUp={endPreviewResize}
          onPointerCancel={endPreviewResize}
          onDoubleClick={() => setPreviewWidth(PREVIEW_DEFAULT_WIDTH)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setPreviewWidth((w) => clampPreviewWidth(w + 24, splitRef.current));
            if (e.key === 'ArrowRight') setPreviewWidth((w) => clampPreviewWidth(w - 24, splitRef.current));
          }}
          className="group mx-2 hidden w-3 shrink-0 cursor-col-resize touch-none select-none items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:flex"
        >
          <div className="h-16 w-1 rounded-full bg-border transition-colors group-hover:bg-foreground/50 group-active:bg-foreground/70" />
        </div>

        {/* Right: live phone preview (stays in view on wide screens) */}
        <div
          className="flex w-full shrink-0 flex-col lg:w-[var(--preview-w)] lg:overflow-y-auto lg:pr-1"
          style={{ ['--preview-w' as string]: `${previewWidth}px` }}
        >
          <h2 className="mb-2 shrink-0 text-sm font-medium">{t('women.preview.title')}</h2>
          <StoryPreview
            woman={storyWoman}
            cards={form.storyCards}
            pageIndex={previewIndex}
            onPageIndexChange={setPreviewIndex}
          />
        </div>
      </div>

      <ConfirmDialog
        open={confirmDraft}
        onOpenChange={setConfirmDraft}
        title={t('women.editor.draft.confirmTitle')}
        description={t('women.editor.draft.confirmDescription')}
        confirmLabel={t('women.editor.draft.button')}
        destructive
        onConfirm={() => void requestDraft()}
      />
      <ConfirmDialog
        open={confirmLeave}
        onOpenChange={setConfirmLeave}
        description={t('women.editor.leaveConfirm')}
        confirmLabel={t('women.editor.leave')}
        destructive
        onConfirm={() => navigate({ to: '/women' })}
      />
    </div>
  );
}
